import { Capacitor } from '@capacitor/core';
import type { MobileVersionGateResponseDto } from '@supply-mover-2-0/shared';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';

/**
 * PD-15630 — the native apps' version gate (design mockups on the ticket).
 *
 * Mounted once in the app shell (crm-web + ecommerce-web) alongside
 * AppUpdateBanner, whose service deliberately no-ops on native — this is
 * the native counterpart. Inert in plain browsers: it renders nothing and
 * calls nothing unless Capacitor reports a native platform.
 *
 * The check asks the public crm-api endpoint whether a newer build of THIS
 * app (bundle id + installed version from App.getInfo()) is actually
 * downloadable on its channel — the server verifies real store/TestFlight
 * availability, so a version waiting for App Store approval never prompts.
 *
 * - update_required → full-screen blocking screen; the only path forward
 *   is the store button.
 * - update_available → dismissible top banner; dismissal is remembered per
 *   offered version.
 *
 * Colors are fixed to the light design-mockup palette on purpose — these
 * surfaces mirror the store look, not the in-app theme.
 */

interface NativeUpdateGateProps {
	/**
	 * crm-api base (VITE_API_URL) — the endpoint is public/tenant-less.
	 * Accepts either form in use across the apps: bare origin
	 * (ecommerce-web) or origin + `/api` (crm-web); normalized below.
	 */
	apiBaseUrl: string;
}

/** Reduce either VITE_API_URL form to the bare crm-api origin. */
function normalizeApiOrigin(apiBaseUrl: string): string {
	return apiBaseUrl.replace(/\/+$/, '').replace(/\/api$/, '');
}

const DISMISS_KEY_PREFIX = 'workd:native-update-dismissed:';
const MIN_CHECK_INTERVAL_MS = 5 * 60 * 1000;

interface GateStateModel {
	response: MobileVersionGateResponseDto;
	installedVersion: string;
}

export function NativeUpdateGate({
	apiBaseUrl,
}: NativeUpdateGateProps): ReactElement | null {
	const { t } = useTranslation();
	const [gate, setGate] = useState<GateStateModel | null>(null);
	const [dismissed, setDismissed] = useState(false);
	const lastCheckRef = useRef(0);

	useEffect(() => {
		if (!Capacitor.isNativePlatform()) {
			return;
		}
		// Object-held flag: the async closures below mutate it after the
		// cleanup may have run, which a plain boolean local can't express
		// without tripping no-unnecessary-condition.
		const lifecycle: {
			disposed: boolean;
			removeListener: (() => void) | null;
		} = { disposed: false, removeListener: null };

		const check = async (): Promise<void> => {
			if (Date.now() - lastCheckRef.current < MIN_CHECK_INTERVAL_MS) {
				return;
			}
			lastCheckRef.current = Date.now();
			try {
				const { App } = await import('@capacitor/app');
				const info = await App.getInfo();
				const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
				const response = await fetch(
					`${normalizeApiOrigin(apiBaseUrl)}/api/mobile-apps/version-gate?appId=${encodeURIComponent(info.id)}&platform=${platform}&version=${encodeURIComponent(info.version)}`,
				);
				if (!response.ok || lifecycle.disposed) {
					return;
				}
				const dto = (await response.json()) as MobileVersionGateResponseDto;
				if (dto.status === 'ok' || dto.updateUrl == null) {
					setGate(null);
					return;
				}
				if (
					dto.status === 'update_available' &&
					dto.latestVersion != null &&
					localStorage.getItem(`${DISMISS_KEY_PREFIX}${dto.latestVersion}`) !=
						null
				) {
					setDismissed(true);
				} else {
					setDismissed(false);
				}
				setGate({ response: dto, installedVersion: info.version });
			} catch {
				// Offline / endpoint unavailable — never block or prompt.
			}
		};

		void check();
		void (async (): Promise<void> => {
			try {
				const { App } = await import('@capacitor/app');
				const listener = await App.addListener('appStateChange', (state) => {
					if (state.isActive) {
						void check();
					}
				});
				if (lifecycle.disposed) {
					void listener.remove();
				} else {
					lifecycle.removeListener = (): void => {
						void listener.remove();
					};
				}
			} catch {
				// Plugin unavailable — launch-time check already ran.
			}
		})();

		return (): void => {
			lifecycle.disposed = true;
			lifecycle.removeListener?.();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (gate == null || gate.response.updateUrl == null) {
		return null;
	}
	const { response, installedVersion } = gate;
	const name = response.displayName ?? t('this app');
	const storeLabel =
		Capacitor.getPlatform() === 'ios' ? t('App Store') : t('Google Play');
	const openStore = (): void => {
		if (response.updateUrl != null) {
			window.open(response.updateUrl, '_blank');
		}
	};

	if (response.status === 'update_required') {
		return (
			<div
				role="alertdialog"
				aria-modal="true"
				aria-label={t('Update required')}
				className="fixed inset-0 z-[10000] flex flex-col items-center justify-center px-8 text-center"
				style={{
					backgroundColor: '#ffffff',
					paddingTop: 'env(safe-area-inset-top)',
					paddingBottom: 'env(safe-area-inset-bottom)',
				}}
			>
				{response.iconUrl != null ? (
					<img
						src={response.iconUrl}
						alt=""
						className="w-24 h-24 rounded-3xl shadow-lg mb-10 object-cover"
						onError={(event) => {
							event.currentTarget.style.display = 'none';
						}}
					/>
				) : null}
				<h1
					className="text-3xl font-extrabold mb-6"
					style={{ color: '#111827' }}
				>
					{t('Update required')}
				</h1>
				<p className="text-base mb-10 max-w-sm" style={{ color: '#4b5563' }}>
					{t(
						`This version of ${name} is no longer supported. Update to the latest version to keep working.`,
					)}
				</p>
				<button
					type="button"
					onClick={openStore}
					className="w-full max-w-sm rounded-xl py-3.5 text-base font-semibold"
					style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
				>
					{t(`Update on the ${storeLabel}`)}
				</button>
				{response.latestVersion != null ? (
					<p className="text-sm mt-8" style={{ color: '#9ca3af' }}>
						{t(
							`Version ${installedVersion} installed · ${response.latestVersion} available`,
						)}
					</p>
				) : null}
			</div>
		);
	}

	if (dismissed) {
		return null;
	}
	return (
		<div
			role="status"
			className="flex items-center gap-3 px-4 py-2.5 border-b"
			style={{
				backgroundColor: '#eff6ff',
				borderColor: '#bfdbfe',
				paddingTop: 'calc(env(safe-area-inset-top) + 0.625rem)',
			}}
		>
			{response.iconUrl != null ? (
				<img
					src={response.iconUrl}
					alt=""
					className="flex-none w-10 h-10 rounded-lg object-cover"
					onError={(event) => {
						event.currentTarget.style.display = 'none';
					}}
				/>
			) : null}
			<span
				className="flex-1 min-w-0 text-sm font-medium"
				style={{ color: '#1d4ed8' }}
			>
				{t(`A new version of ${name} is available`)}
			</span>
			<button
				type="button"
				onClick={openStore}
				className="flex-none rounded-full px-4 py-1.5 text-sm font-semibold"
				style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
			>
				{t('Update')}
			</button>
			<button
				type="button"
				aria-label={t('Dismiss')}
				onClick={() => {
					setDismissed(true);
					if (response.latestVersion != null) {
						try {
							localStorage.setItem(
								`${DISMISS_KEY_PREFIX}${response.latestVersion}`,
								'1',
							);
						} catch {
							// Storage unavailable — dismiss for this session only.
						}
					}
				}}
				className="flex-none text-lg px-1"
				style={{ color: '#60a5fa' }}
			>
				&times;
			</button>
		</div>
	);
}
